import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export function ThreeAnimation({ enableControls = true }) {
    const mountRef = useRef(null);
    const animationFrameId = useRef(null); // To store the requestAnimationFrame ID
    const controlsRef = useRef(null); // Add a ref for controls
    // Refs to store mouse position and target rotation
    const mouseX = useRef(0);
    const mouseY = useRef(0);
    const targetRotationX = useRef(0);
    const targetRotationY = useRef(0);
    const windowHalfX = useRef(window.innerWidth / 2);
    const windowHalfY = useRef(window.innerHeight / 2);
    
    // Check if device is mobile
    const isMobile = useMemo(() => {
        if (typeof window !== 'undefined') {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
        }
        return false;
    }, []);
    
    // Disable controls on mobile devices
    const shouldEnableControls = enableControls && !isMobile;

    useEffect(() => {
        if (!mountRef.current) return;

        const currentMount = mountRef.current; // Capture mountRef.current

        let group;
        let camera, scene, renderer;
        let particlesData = [];
        let positions, colors;
        let particles;
        let pointCloud;
        let particlePositions;
        let linesMesh;
        let lc = new THREE.Color("#16B08B"); // Green line color
        let controls; // Declare controls variable

        const maxParticleCount = 100; // Reduced max count
        let particleCount = 120;      // Reduced initial count
        const r = 460;                // Slightly smaller radius
        const rHalf = r / 2;
        let v3 = new THREE.Vector3();

        const effectController = {
            showDots: true,
            showLines: true,
            minDistance: 210, // Slightly increased min distance
            limitConnections: false,
            maxConnections: 5, // Reduced max connections
            particleCount: 20 // Initial count matches variable
        };

        // Mouse move listener
        function onDocumentMouseMove(event) {
            mouseX.current = (event.clientX - windowHalfX.current);
            mouseY.current = (event.clientY - windowHalfY.current);
            // Calculate target rotation based on mouse position (adjust factor for sensitivity)
            targetRotationX.current = mouseY.current * 0.0005;
            targetRotationY.current = mouseX.current * 0.0005;
        }

        // Window resize listener also needs to update windowHalf refs
        function onWindowResizeInternal() {
            if (!currentMount || !renderer || !camera) return;
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;

            if (width === 0 || height === 0) return;

            windowHalfX.current = width / 2; // Update ref
            windowHalfY.current = height / 2; // Update ref

            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height);
        }

        function init() {
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;

            // Guard against zero dimensions which can cause issues
            if (width === 0 || height === 0) return;

            // Update window half refs initially
            windowHalfX.current = width / 2;
            windowHalfY.current = height / 2;

            camera = new THREE.PerspectiveCamera(55, width / height, 1, 1000); // Adjusted far plane
            camera.position.z = 600; // Adjusted camera distance

            scene = new THREE.Scene();
            group = new THREE.Group();
            scene.add(group);

            // Add a wireframe sphere helper matching the particle boundary
            const sphereGeometry = new THREE.SphereGeometry(rHalf, 32, 16); // Use SphereGeometry
            const sphereMaterial = new THREE.MeshBasicMaterial({
                color: 0x30ffff0, // Cyan color for visibility
                wireframe: true,
                transparent: true,
                opacity: 0.1
            });
            const helperSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            group.add(helperSphere); // Add the sphere to the group

            const segments = maxParticleCount * maxParticleCount;
            positions = new Float32Array(segments * 3);
            colors = new Float32Array(segments * 3);

            const pMaterial = new THREE.PointsMaterial({
                color: 0x00FF88, // Keep particle color green
                size: 7,         // Slightly smaller particles
                blending: THREE.AdditiveBlending,
                transparent: true,
                sizeAttenuation: true // Make size attenuate with distance
            });

            particles = new THREE.BufferGeometry();
            particlePositions = new Float32Array(maxParticleCount * 3);

            for (let i = 0; i < maxParticleCount; i++) {
                let rand = Math.random();
                let radius = Math.cbrt(rand) * rHalf; // Distribute particles more evenly within the sphere volume
                v3.randomDirection().setLength(radius);

                particlePositions[i * 3] = v3.x;
                particlePositions[i * 3 + 1] = v3.y;
                particlePositions[i * 3 + 2] = v3.z;

                particlesData.push({
                    velocity: new THREE.Vector3().randomDirection().multiplyScalar(Math.random() * 0.5 + 0.1), // Slower velocity
                    numConnections: 0
                });
            }

            particles.setDrawRange(0, particleCount);
            particles.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3).setUsage(THREE.DynamicDrawUsage));

            pointCloud = new THREE.Points(particles, pMaterial);
            if (effectController.showDots) { // Only add if showDots is true initially
               group.add(pointCloud);
            }


            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage));
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3).setUsage(THREE.DynamicDrawUsage));
            geometry.computeBoundingSphere();
            geometry.setDrawRange(0, 0);

            const material = new THREE.LineBasicMaterial({
                vertexColors: true,
                blending: THREE.AdditiveBlending,
                transparent: true,
                opacity: 0.4 // Make lines slightly transparent
            });

            linesMesh = new THREE.LineSegments(geometry, material);
            if (effectController.showLines) { // Only add if showLines is true initially
               group.add(linesMesh);
            }


            renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); // Enable alpha for transparent background
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.setSize(width, height);
            renderer.outputColorSpace = THREE.SRGBColorSpace; // Use the new property

            currentMount.appendChild(renderer.domElement);

            // Allow scroll/tap on mobile, avoid Firefox/Chrome gesture issues
            renderer.domElement.style.touchAction = 'manipulation';
            renderer.domElement.style.pointerEvents = 'auto';

            // Optional: reassure Firefox that canvas is passive
            renderer.domElement.addEventListener('touchstart', () => {}, { passive: true });

            // Initialize OrbitControls only if not on mobile
            if (shouldEnableControls) {
                controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true; // Enable damping (inertia)
                controls.dampingFactor = 0.05; // Adjust damping factor
                controls.screenSpacePanning = false; // Disallow panning
                // Optional: Set min/max distance if needed
                // controls.minDistance = 500;
                // controls.maxDistance = 1500;
                controls.enableZoom = false; // Disable zooming
                controls.enablePan = false; // Disable panning
                controlsRef.current = controls; // Store controls in ref

                // --- Add grab/grabbing cursor logic ---
                currentMount.style.cursor = 'grab'; // Default grab cursor on hover

                controls.addEventListener('start', () => {
                    currentMount.style.cursor = 'grabbing'; // Change to grabbing when dragging starts
                });
                controls.addEventListener('end', () => {
                    currentMount.style.cursor = 'grab'; // Change back to grab when dragging ends
                });
                // --- End cursor logic ---
            }

            // Add listeners
            if (!isMobile) {
                document.addEventListener('mousemove', onDocumentMouseMove);
              }

            window.addEventListener('resize', onWindowResizeInternal); // Use internal wrapper
        }

        function onWindowResize() {
             if (!currentMount || !renderer || !camera) return;
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;

            // Guard against zero dimensions
            if (width === 0 || height === 0) return;


            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height);
        }

        function animate() {
            animationFrameId.current = requestAnimationFrame(animate); // Store the ID

             // Check if renderer and other necessary objects exist
             if (!renderer || !scene || !camera || !particlePositions || !particlesData || !linesMesh || !pointCloud || !group) {
                console.warn("Three.js objects not fully initialized in animate.");
                return;
             }

            // --- Apply mouse-based rotation smoothly ---
            if (group) { // Check if group exists
                group.rotation.y += (targetRotationY.current - group.rotation.y) * 1.7; // Smooth interpolation
                group.rotation.x += (targetRotationX.current - group.rotation.x) * 1.7; // Smooth interpolation
            }
            // --- End mouse rotation ---

            // Update controls if they exist and damping is enabled
            if (controlsRef.current && controlsRef.current.enableDamping) {
                controlsRef.current.update();
            }

            let vertexpos = 0;
            let colorpos = 0;
            let numConnected = 0;

            // Ensure particleCount is within bounds
            const currentParticleCount = Math.min(particleCount, maxParticleCount);


            for (let i = 0; i < currentParticleCount; i++)
                if (particlesData[i]) particlesData[i].numConnections = 0; // Check if exists

            for (let i = 0; i < currentParticleCount; i++) {
                 // Add checks for particleData existence
                 const particleData = particlesData[i];
                 if (!particleData) continue;


                particlePositions[i * 3] += particleData.velocity.x;
                particlePositions[i * 3 + 1] += particleData.velocity.y;
                particlePositions[i * 3 + 2] += particleData.velocity.z;

                v3.fromArray(particlePositions, i * 3);
                let v3len = v3.length();

                // Bounce off sphere boundary
                if (v3len > rHalf) {
                     v3.normalize();
                     particleData.velocity.reflect(v3);
                     // Ensure particle stays within bounds after reflection
                     v3.multiplyScalar(rHalf);
                     particlePositions[i * 3] = v3.x;
                     particlePositions[i * 3 + 1] = v3.y;
                     particlePositions[i * 3 + 2] = v3.z;
                }


                if (effectController.limitConnections && particleData.numConnections >= effectController.maxConnections)
                    continue;

                // Check collision
                for (let j = i + 1; j < currentParticleCount; j++) {
                     // Check particleDataB existence
                    const particleDataB = particlesData[j];
                    if (!particleDataB) continue;

                    if (effectController.limitConnections && particleDataB.numConnections >= effectController.maxConnections)
                        continue;

                    const dx = particlePositions[i * 3] - particlePositions[j * 3];
                    const dy = particlePositions[i * 3 + 1] - particlePositions[j * 3 + 1];
                    const dz = particlePositions[i * 3 + 2] - particlePositions[j * 3 + 2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (dist < effectController.minDistance) {
                         // Ensure vertexpos and colorpos don't exceed buffer lengths
                        if ((vertexpos + 5) < positions.length && (colorpos + 5) < colors.length) {
                            particleData.numConnections++;
                            particleDataB.numConnections++;

                            const alpha = 1.0 - dist / effectController.minDistance;

                            positions[vertexpos++] = particlePositions[i * 3];
                            positions[vertexpos++] = particlePositions[i * 3 + 1];
                            positions[vertexpos++] = particlePositions[i * 3 + 2];

                            positions[vertexpos++] = particlePositions[j * 3];
                            positions[vertexpos++] = particlePositions[j * 3 + 1];
                            positions[vertexpos++] = particlePositions[j * 3 + 2];

                            // Use line color (lc)
                            colors[colorpos++] = alpha * lc.r;
                            colors[colorpos++] = alpha * lc.g;
                            colors[colorpos++] = alpha * lc.b;

                            colors[colorpos++] = alpha * lc.r;
                            colors[colorpos++] = alpha * lc.g;
                            colors[colorpos++] = alpha * lc.b;

                            numConnected++;
                        } else {
                             console.warn("Buffer boundary reached in animate loop.");
                             break; // Exit inner loop if buffer is full
                        }
                    }
                }
                 if (vertexpos >= positions.length || colorpos >= colors.length) {
                    break; // Exit outer loop if buffer is full
                 }
            }


            linesMesh.geometry.setDrawRange(0, numConnected * 2);
            if (linesMesh.geometry.attributes.position) linesMesh.geometry.attributes.position.needsUpdate = true;
            if (linesMesh.geometry.attributes.color) linesMesh.geometry.attributes.color.needsUpdate = true;

            if (pointCloud.geometry.attributes.position) pointCloud.geometry.attributes.position.needsUpdate = true;


            render();
        }

        function render() {
             // Check if renderer and other necessary objects exist
             if (!renderer || !scene || !camera || !group) {
                 console.warn("Three.js objects not fully initialized in render.");
                 return;
             }
            // const time = Date.now() * 0.0004; // Removed automatic time-based rotation
            // group.rotation.y = time;
            renderer.render(scene, camera);
        }

        init();
         // Only start animation loop if init was successful (e.g., width/height > 0)
         if (renderer) {
            animate();
         }


        // --- Cleanup function ---
        return () => {
            // Remove listeners
            
            if (!isMobile) {
                document.removeEventListener('mousemove', onDocumentMouseMove);
              }

            window.removeEventListener('resize', onWindowResizeInternal); // Use internal wrapper

            // --- Remove controls listeners ---
            if (controlsRef.current) {
                controlsRef.current.removeEventListener('start'); // Clean up listeners
                controlsRef.current.removeEventListener('end');
                controlsRef.current.dispose();
            }
            // --- End controls listener cleanup ---


            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current); // Cancel the animation frame
            }
             if (renderer) {
                 // Properly dispose of Three.js objects
                 if (scene) {
                    scene.traverse((object) => {
                        if (!object) return;
                         if (object.geometry) object.geometry.dispose();
                         if (object.material) {
                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => material && material.dispose());
                            } else if (object.material.dispose) {
                                object.material.dispose();
                            }
                         }
                    });
                 }
                 if (currentMount && renderer.domElement && currentMount.contains(renderer.domElement)) {
                    // Reset cursor style before removing element
                    currentMount.style.cursor = 'auto';
                    currentMount.removeChild(renderer.domElement);
                 }
                 renderer.dispose(); // Dispose renderer resources
             }
             // Nullify references
             camera = null;
             scene = null;
             renderer = null;
             group = null;
             particlesData = [];
             positions = null;
             colors = null;
             particles = null;
             pointCloud = null;
             particlePositions = null;
             linesMesh = null;
             controls = null;
             controlsRef.current = null;
             // Reset mouse refs (optional but good practice)
             mouseX.current = 0;
             mouseY.current = 0;
             targetRotationX.current = 0;
             targetRotationY.current = 0;

        };
    }, [shouldEnableControls]); // Add shouldEnableControls as dependency

    // Ensure the div takes up the full space given by its parent
    return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
}